import { Component, ViewContainerRef, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { DialogService } from '../../../_services/dialog.service';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { NgxSpinnerService } from 'ngx-spinner';
import { PledgePerWinDetailsModalComponent } from '../ppw-details-modal-component';
import { PledgePerWinGameCompletionModalComponent } from './ppw-game-completion-modal.component';
import { PledgePerWinService } from '../../../_services/fundraising/pledgePerWin.service';
import { PledgePerWin } from '../../../_models/fundraising/ppw/PledgePerWin';
import { forkJoin, of } from 'rxjs';
import { Game, GameGroup } from '../../../_models/fundraising/ppw/Game';

@Component({
  selector: 'app-ppw-game-management',
  templateUrl: './ppw-game-management.component.html',
  styleUrls: ['../../fundraising-management.component.css', './ppw-game-management.component.css']
})

export class PledgePerWinGameManagementComponent implements OnInit  {

  loading = false;
  searchText: string;

  public games: Game[] = [];
  public gameGroups: GameGroup[] = [];
  public displayItems: Game[] = [];
  public ppws: PledgePerWin[] = [];

  public selectedCampaign = 0;

  private bsModalRef: BsModalRef;

  constructor(
    private ppwService: PledgePerWinService,
    private router: Router,
    private viewRef: ViewContainerRef,
    private dialogService: DialogService,
    private spinner: NgxSpinnerService,
    private modalService: BsModalService
  ) { }

  ngOnInit() {
    this.loadGames();
  }

  loadGames() {
    this.loading = true;
    this.spinner.show();

    const ppwObs = this.ppwService.getAll();
    const gameObs = this.ppwService.getAllGames();

    forkJoin([ppwObs, gameObs]).subscribe(data => {
      this.games = data[1];
      this.gameGroups = this.createGameGroups([...this.games], null);

      this.ppws = data[0].pledgePerWins;

      this.loading = false;
      this.spinner.hide();
    },
    error => {   
      this.loading = false;
      this.spinner.hide();

      if (!error) return;

      this.dialogService.alert(
        'Uh oh something went wrong...', 
        `Unable to load games: ${error.error}`,
        this.viewRef);

    });
  }

  public create() {
    const modalConfig = {
      backdrop: true,
      ignoreBackdropClick: true
    };

    // No initial state
    const initialState = {
      gameId: -1,
      viewRef: this.viewRef
    };

    this.bsModalRef = this.modalService
      .show(
        PledgePerWinGameCompletionModalComponent,
        Object.assign({}, modalConfig, { class: 'modal-md', initialState }));

    this.bsModalRef.content.charged
        .subscribe(_ => {
          this.ngOnInit();
        });
  }

  public view(gameId: number){
    const game = this.games.find(g => g.pledgePerWinGameId === gameId);

    const modalConfig = {
      backdrop: true,
      ignoreBackdropClick: true
    };

    // Pass game since we're just viewing
    const initialState = {
      game, 
      viewRef: this.viewRef
    };

    this.bsModalRef = this.modalService
      .show(
        PledgePerWinGameCompletionModalComponent,
        Object.assign({}, modalConfig, { class: 'modal-md', initialState }));
  }

  reverseCharges(gameId: number) {
    this.dialogService.confirm("Are you sure?", "This will reverse all charges for this game and cannot be undone", this.viewRef)
      .then(res => {
        if (res){
          this.spinner.show();
          this.ppwService.reverseCharges(gameId)
            .subscribe(res => {
              this.spinner.hide();
              this.dialogService.alert("Success", `The charges for this game have been successfully reversed. ${res.totalFailedReversals} transaction(s) failed to refund. If there were any, you'll be emailed a report on failed refunds`, this.viewRef)
                .then(_ => {
                  this.ngOnInit();
                });
            }, error => {
              this.dialogService.alert("Uh oh!", `Something went wrong trying to reverse charges for this game: ${error.error}`, this.viewRef);
            })
        }
      })

  }

  filterByCampaign () {

    this.gameGroups = this.createGameGroups([...this.games], null);
    
    if (+this.selectedCampaign === 0){

      return;
    }

    this.gameGroups = this.gameGroups.filter(g => g.children[0].pledgePerWinId === +this.selectedCampaign);
  }

  public search() {

    this.gameGroups = this.createGameGroups([...this.games], null);

    if (!this.searchText) {

      return;
    }
    
    let quoteRegex = /[^\s"]+|"([^"]*)"/gi;
    let searchTerms = [];
    let match;

    do {

      match = quoteRegex.exec(this.searchText.toLowerCase());

      if (match != null) {

        searchTerms.push(match[1] ? match[1] : match[0]);
      }

    } while (match != null);

    this.gameGroups = this.createGameGroups([...this.games], searchTerms)
  }

  public createGameGroups (games: Game[], filters: string[] = null) : GameGroup[] {

    const groups = games.reduce(
      (r, e) => {

        // Find the group this track belongs
        let group = e.pledgePerWin.event.name;

        const match = (filters && filters.some(f => e.name.toLowerCase().indexOf(f) > -1) || !filters);

        // If there's no group for this campaign yet, we are filtering and the game name matches the search term, or we aren't filtering, create a new group and add game as child
        if (!r[group] && match) {

          r[group] = {group, children: [e]} as GameGroup;

        // If there is a group for this campaign, we are filtering and the game name matches the search term, or we aren't filtering, add this game to children of the group
        } else if (r[group] && match) {

          r[group].children.push(e);
        }

        return r;
      }, {});

    const gameGroups = Object.values(groups) as GameGroup[];

    // Sort the games in each group by date completed
    for(let group of gameGroups) {

      group.children = group.children.sort((a, b) => new Date(b.dateCompleted).getTime() - new Date(a.dateCompleted).getTime());
    }

    // Sort each group by the most recent game
    gameGroups.sort((a, b) => new Date(b.children[0].dateCompleted).getTime() - new Date(a.children[0].dateCompleted).getTime());

    let completedGroups = [];

    // Find out which groups are campaigns that have been completed or cancelled
    for(let group of gameGroups) {

      if (group.children[0].pledgePerWin.event.status > 2) {
        
        const idx = gameGroups.indexOf(group);
        completedGroups = completedGroups.concat(gameGroups.splice(idx, 1));
      }
    }

    // Put the completed campaigns at the end of the list
    return gameGroups.concat(completedGroups);
  }

  public resendReportEmail (pledgePerWinGameId: number) {

    this.spinner.show();

    this.ppwService.resendGameReportEmail(pledgePerWinGameId)
      .subscribe(
        _ => {

          this.spinner.hide();
          this.dialogService.alert("Sent!", "An email containing the after-game report has been sent to your inbox.", this.viewRef);
        },
        error => {

          this.spinner.hide();
          this.dialogService.alert("Uh oh!", `Something went wrong trying to send that email. Please contact support at support@connectwithcue.com. ${error.error}`, this.viewRef);
        }
      )
  }
}
